import { useMemo } from 'react';

import { CHART_MAX_VALUE, CHART_MIN_VALUE } from '../../constants';
import { IChartMeasurement } from '../../interfaces';
import { useDateRange, usePhysiologicalData } from '../physiological-data';
import { parsePhysiologicalMeasurements } from './utils';

export function useChartMeasurements(): IChartMeasurement[] {
  const { physiologicalData, rangeType } = usePhysiologicalData();
  const { dateFrom, dateTo } = useDateRange();

  return useMemo(() => {
    return parsePhysiologicalMeasurements(physiologicalData?.measurements ?? [], rangeType, dateFrom, dateTo);
  }, [dateFrom, dateTo, physiologicalData?.measurements, rangeType]);
}

export function useYAxisTicks() {
  const measurements = useChartMeasurements();

  return useMemo(() => {
    const range = measurements.reduce(
      (range, entry) => {
        const minSys = entry.sys ? entry.sys - 10 : CHART_MIN_VALUE;
        const minDia = entry.dia ? entry.dia - 10 : CHART_MIN_VALUE;
        const maxSys = entry.sys ? entry.sys + 10 : CHART_MAX_VALUE;
        const maxDia = entry.dia ? entry.dia + 10 : CHART_MAX_VALUE;
        const min = Math.floor(Math.min(range.min, minSys, minDia) / 10) * 10;
        const max = Math.ceil(Math.max(range.max, maxSys, maxDia) / 10) * 10;
        return { min, max };
      },
      { min: CHART_MIN_VALUE, max: CHART_MAX_VALUE },
    );

    return Array.from(Array((range.max - range.min) / 10 + 1), (_, i) => range.min + i * 10);
  }, [measurements]);
}

export function useXAxisTicks(): string[] {
  const measurements = useChartMeasurements();

  return useMemo(() => {
    return measurements.map((m: IChartMeasurement) => m.date);
  }, [measurements]);
}
